/**
 * Our Brands Hero – On-Load Reveal Animation
 *
 * Timeline played on page load:
 *   1. Heading fades / slides up
 *   2. Subtitle fades / slides up (overlapping)
 *   3. Product image pops up from below the container bottom
 *
 * Respects prefers-reduced-motion, includes GSAP polling + fallback timer,
 * and cleans up on disconnect.
 */

class OurBrandsHeroReveal extends HTMLElement {
  constructor() {
    super();
    this.timeline = null;
    this.fallbackTimer = null;
  }

  connectedCallback() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.showFallback();
      return;
    }

    if (document.readyState === 'complete') {
      this.init();
    } else {
      window.addEventListener('load', () => this.init());
    }

    /* Safety fallback — reveal after 5 s if GSAP never loads */
    this.fallbackTimer = setTimeout(() => this.showFallback(), 5000);
  }

  /* -------- GSAP helpers -------- */

  getGSAP() {
    if (typeof window !== 'undefined' && window.gsap && typeof window.gsap.to === 'function') {
      return window.gsap;
    }
    if (typeof window !== 'undefined' && window.GSAP && typeof window.GSAP.to === 'function') {
      return window.GSAP;
    }
    if (typeof window !== 'undefined' && window.GreenSockGlobals && window.GreenSockGlobals.gsap) {
      return window.GreenSockGlobals.gsap;
    }
    if (typeof gsap !== 'undefined' && typeof gsap.to === 'function') {
      return gsap;
    }
    return null;
  }

  waitForGSAP() {
    return new Promise((resolve) => {
      if (this.getGSAP()) {
        resolve();
        return;
      }

      var attempts = 0;
      var check = setInterval(() => {
        attempts++;
        if (this.getGSAP() || attempts > 50) {
          clearInterval(check);
          resolve();
        }
      }, 100);
    });
  }

  /* -------- Initialisation -------- */

  async init() {
    await this.waitForGSAP();
    var gsapLib = this.getGSAP();

    if (!gsapLib) {
      this.showFallback();
      return;
    }

    /* Cancel safety fallback */
    if (this.fallbackTimer) {
      clearTimeout(this.fallbackTimer);
      this.fallbackTimer = null;
    }

    var sectionId = this.getAttribute('data-section-id');
    var section = document.getElementById('OurBrandsHero-' + sectionId);
    if (!section) return;

    var heading = section.querySelector('.our-brands-hero__heading');
    var subtitle = section.querySelector('.our-brands-hero__subtitle');
    var imageContainer = section.querySelector('.our-brands-hero__image-container');

    if (!heading && !subtitle && !imageContainer) {
      this.showFallback();
      return;
    }

    /* Build on-load timeline */
    this.timeline = gsapLib.timeline({ delay: 0.15 });

    /* 1. Heading — fade & slide up */
    if (heading) {
      this.timeline.fromTo(heading,
        { opacity: 0, y: 40 },
        { opacity: 1, y: 0, duration: 0.7, ease: 'power3.out' },
        0
      );
    }

    /* 2. Subtitle — fade & slide up, overlapping with heading */
    if (subtitle) {
      this.timeline.fromTo(subtitle,
        { opacity: 0, y: 30 },
        { opacity: 1, y: 0, duration: 0.6, ease: 'power3.out' },
        0.2
      );
    }

    /* 3. Product image — pop up from below the container */
    if (imageContainer) {
      this.timeline.fromTo(imageContainer,
        { opacity: 0, y: 100, scale: 0.95 },
        { opacity: 1, y: 0, scale: 1, duration: 0.9, ease: 'power3.out' },
        0.35
      );
    }
  }

  /* -------- Fallback -------- */

  showFallback() {
    var sectionId = this.getAttribute('data-section-id');
    var section = document.getElementById('OurBrandsHero-' + sectionId);
    if (!section) return;

    var els = section.querySelectorAll(
      '.our-brands-hero__heading, .our-brands-hero__subtitle, .our-brands-hero__image-container'
    );
    els.forEach(function (el) {
      el.style.opacity = '1';
      el.style.transform = 'none';
    });
  }

  /* -------- Cleanup -------- */

  disconnectedCallback() {
    if (this.timeline && this.timeline.kill) {
      this.timeline.kill();
    }
    if (this.fallbackTimer) {
      clearTimeout(this.fallbackTimer);
    }
  }
}

if (!customElements.get('our-brands-hero-reveal')) {
  customElements.define('our-brands-hero-reveal', OurBrandsHeroReveal);
}


